import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, TextField, Divider, Alert,
} from '@mui/material';
import { Save as SaveIcon, UploadFile as UploadFileIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import LogoUploader from '../../layouts/LogoUploader';

/**
 * Branding del portal/correos (ATS), configurado por proyecto.
 * Para el nombre y el logo se elige la fuente: organización, proyecto,
 * personalizado o no mostrar. Por defecto no se muestra nada (opt-in).
 */
const AtsBrandingTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [nameSource, setNameSource] = useState('none');
  const [customName, setCustomName] = useState('');
  const [logoSource, setLogoSource] = useState('none');
  const [customLogoUrl, setCustomLogoUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const bannerInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await api.get(`/projects/ats-branding`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const d = res.data.response?.data;
        if (d) {
          setNameSource(d.nameSource || 'none');
          setCustomName(d.customName || '');
          setLogoSource(d.logoSource || 'none');
          setCustomLogoUrl(d.customLogoUrl || '');
          setBannerUrl(d.bannerUrl || '');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.branding.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Relación ancho:alto mínima recomendada para el banner. Bajo este valor
  // (imágenes cuadradas, verticales o poco panorámicas) se avisa — sin
  // bloquear — que en el portal se verá solo una franja central, porque el
  // banner se renderiza a ancho completo con altura máxima y recorte vertical.
  const MIN_BANNER_RATIO = 3;

  // Mide la relación ancho/alto de la imagen en el navegador.
  // Si no se puede medir, resuelve null y simplemente no se muestra el aviso.
  const getImageRatio = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });

  const handleBannerFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      showAlert(t('branding.invalidImage'), 'error');
      return;
    }
    setUploadingBanner(true);
    try {
      const ratio = await getImageRatio(file);
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post(`/positions/branding-logo`, fd, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.response?.data?.url || null;
      if (res.data.response?.status_response === 'OK' && url) {
        setBannerUrl(url);
        if (ratio !== null && ratio < MIN_BANNER_RATIO) {
          showAlert(t('ats.config.branding.bannerRatioWarning'), 'warning');
        }
      } else {
        showAlert(t('ats.config.branding.bannerError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.branding.bannerError'), 'error');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put(
        `/projects/ats-branding`,
        { nameSource, customName, logoSource, customLogoUrl, bannerUrl },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        showAlert(t('ats.config.branding.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.branding.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.branding.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const SOURCE_OPTIONS = [
    { value: 'none', label: t('ats.config.branding.sourceNone') },
    { value: 'organization', label: t('ats.config.branding.sourceOrganization') },
    { value: 'project', label: t('ats.config.branding.sourceProject') },
    { value: 'custom', label: t('ats.config.branding.sourceCustom') },
  ];

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.branding.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.config.branding.description')}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('ats.config.branding.note')}
      </Alert>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('ats.config.branding.nameTitle')}
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>{t('ats.config.branding.source')}</InputLabel>
        <Select
          value={nameSource}
          label={t('ats.config.branding.source')}
          onChange={(e) => setNameSource(e.target.value)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {nameSource === 'custom' && (
        <TextField
          fullWidth
          size="small"
          label={t('ats.config.branding.customName')}
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('ats.config.branding.logoTitle')}
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>{t('ats.config.branding.source')}</InputLabel>
        <Select
          value={logoSource}
          label={t('ats.config.branding.source')}
          onChange={(e) => setLogoSource(e.target.value)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {logoSource === 'custom' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <LogoUploader
            currentUrl={customLogoUrl || null}
            uploadUrl={`/positions/branding-logo`}
            onUploaded={(url) => setCustomLogoUrl(url)}
            size={64}
            shape="square"
          />
          <Typography variant="caption" color="text.secondary">
            {t('ats.config.branding.customLogoHint')}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {t('ats.config.branding.bannerTitle')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('ats.config.branding.bannerHint')}
      </Typography>
      {bannerUrl ? (
        <Box
          component="img"
          src={bannerUrl}
          alt="banner"
          sx={{
            width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1,
            display: 'block', mb: 1.5, border: '1px solid', borderColor: 'divider',
          }}
        />
      ) : null}
      <input ref={bannerInputRef} type="file" accept="image/*" hidden onChange={handleBannerFile} />
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={uploadingBanner ? <CircularProgress size={14} /> : <UploadFileIcon />}
          onClick={() => bannerInputRef.current && bannerInputRef.current.click()}
          disabled={uploadingBanner}
        >
          {bannerUrl ? t('ats.config.branding.bannerReplace') : t('ats.config.branding.bannerUpload')}
        </Button>
        {bannerUrl && (
          <Button size="small" color="inherit" startIcon={<DeleteIcon />} onClick={() => setBannerUrl('')}>
            {t('ats.config.branding.bannerRemove')}
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.branding.save')}
      </Button>
    </Box>
  );
};

export default AtsBrandingTab;
